import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export type AlertRuleType = 
  | 'order_waiting'
  | 'order_ready'
  | 'kitchen_order_waiting'
  | 'low_stock'
  | 'register_open'
  | 'shift_overtime'
  | 'custom';

export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface AlertGroup {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  member_count?: number;
}

export interface AlertGroupMember {
  id: string;
  alert_group_id: string;
  user_id: string;
  notification_email: boolean;
  notification_in_app: boolean;
  created_at: string;
  user?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    role: string;
  };
}

export interface AlertRuleConditions {
  threshold_minutes?: number;
  order_types?: string[];
  order_statuses?: string[];
  threshold_quantity?: number;
  category_ids?: string[];
  inactive_minutes?: number;
}

export interface AlertRule {
  id: string;
  organization_id: string;
  location_id: string | null;
  name: string;
  description: string | null;
  rule_type: AlertRuleType;
  severity: AlertSeverity;
  conditions: AlertRuleConditions;
  alert_group_id: string | null;
  additional_user_ids: string[];
  send_email: boolean;
  send_in_app: boolean;
  cooldown_minutes: number;
  max_alerts_per_hour: number;
  active_start_time: string | null;
  active_end_time: string | null;
  active_days: number[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  alert_group?: AlertGroup;
  location?: {
    id: string;
    name: string;
  };
}

export interface AlertHistory {
  id: string;
  organization_id: string;
  location_id: string | null;
  alert_rule_id: string | null;
  rule_type: AlertRuleType;
  severity: AlertSeverity;
  title: string;
  message: string;
  entity_type: string | null;
  entity_id: string | null;
  context: Record<string, any>;
  recipients: Array<{
    user_id: string;
    email?: string;
    sent_email: boolean;
    sent_in_app: boolean;
    read_at?: string;
  }>;
  status: 'active' | 'acknowledged' | 'resolved' | 'dismissed';
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_notes: string | null;
  created_at: string;
  location?: {
    id: string;
    name: string;
  };
  alert_rule?: {
    id: string;
    name: string;
  };
}

export interface UserNotification {
  id: string;
  user_id: string;
  organization_id: string;
  alert_history_id: string | null;
  title: string;
  message: string;
  severity: AlertSeverity;
  action_url: string | null;
  action_label: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

// Alert Groups hooks
export function useAlertGroups() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['alert-groups', profile?.organization_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('alert_groups')
        .select(`
          *,
          alert_group_members(count)
        `)
        .eq('organization_id', profile!.organization_id)
        .order('name');

      if (error) throw error;

      return (data || []).map(g => ({
        ...g,
        member_count: g.alert_group_members?.[0]?.count || 0,
      })) as AlertGroup[];
    },
    enabled: !!profile?.organization_id,
  });
}

export function useAlertGroupMembers(groupId: string | null) {
  return useQuery({
    queryKey: ['alert-group-members', groupId],
    queryFn: async () => {
      if (!groupId) return [];

      // First fetch the group members
      const { data: members, error: membersError } = await supabase
        .from('alert_group_members')
        .select('*')
        .eq('alert_group_id', groupId);

      if (membersError) throw membersError;
      if (!members || members.length === 0) return [];

      // Then fetch the profile data for those users
      // Note: profiles table doesn't have email - that's in auth.users
      const userIds = members.map(m => m.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, role')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Map profiles to members
      const profileMap = new Map(profiles?.map(p => [p.id, { ...p, email: null }]) || []);
      
      return members.map(m => ({
        ...m,
        user: profileMap.get(m.user_id) || null,
      })) as AlertGroupMember[];
    },
    enabled: !!groupId,
  });
}

export function useCreateAlertGroup() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const { data: group, error } = await supabase
        .from('alert_groups')
        .insert({
          organization_id: profile!.organization_id,
          name: data.name,
          description: data.description,
        })
        .select()
        .single();

      if (error) throw error;
      return group;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-groups'] });
    },
  });
}

export function useUpdateAlertGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string; name?: string; description?: string; is_active?: boolean }) => {
      const { data: group, error } = await supabase
        .from('alert_groups')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return group;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-groups'] });
    },
  });
}

export function useDeleteAlertGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('alert_groups')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-groups'] });
    },
  });
}

export function useAddGroupMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      alert_group_id: string;
      user_id: string;
      notification_email?: boolean;
      notification_in_app?: boolean;
    }) => {
      const { data: member, error } = await supabase
        .from('alert_group_members')
        .insert({
          alert_group_id: data.alert_group_id,
          user_id: data.user_id,
          notification_email: data.notification_email ?? true,
          notification_in_app: data.notification_in_app ?? true,
        })
        .select()
        .single();

      if (error) {
        // Handle duplicate member (409 Conflict)
        if (error.code === '23505' || error.message?.includes('duplicate') || error.message?.includes('unique')) {
          throw new Error('This user is already a member of this group');
        }
        throw error;
      }
      return member;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['alert-group-members', variables.alert_group_id] });
      queryClient.invalidateQueries({ queryKey: ['alert-groups'] });
    },
  });
}

export function useRemoveGroupMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, groupId }: { id: string; groupId: string }) => {
      const { error } = await supabase
        .from('alert_group_members')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return groupId;
    },
    onSuccess: (groupId) => {
      queryClient.invalidateQueries({ queryKey: ['alert-group-members', groupId] });
      queryClient.invalidateQueries({ queryKey: ['alert-groups'] });
    },
  });
}

// Alert Rules hooks
export function useAlertRules() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['alert-rules', profile?.organization_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('alert_rules')
        .select(`
          *,
          alert_group:alert_groups(id, name),
          location:locations(id, name)
        `)
        .eq('organization_id', profile!.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as AlertRule[];
    },
    enabled: !!profile?.organization_id,
  });
}

export function useCreateAlertRule() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (data: Omit<AlertRule, 'id' | 'organization_id' | 'created_at' | 'updated_at' | 'created_by' | 'alert_group' | 'location'>) => {
      const { data: rule, error } = await supabase
        .from('alert_rules')
        .insert({
          ...data,
          organization_id: profile!.organization_id,
          created_by: profile!.id,
        })
        .select()
        .single();

      if (error) throw error;
      return rule;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
    },
  });
}

export function useUpdateAlertRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<AlertRule> & { id: string }) => {
      const { data: rule, error } = await supabase
        .from('alert_rules')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return rule;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
    },
  });
}

export function useDeleteAlertRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('alert_rules')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
    },
  });
}

// Alert History hooks
export function useAlertHistory(filters?: {
  status?: string;
  severity?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['alert-history', profile?.organization_id, filters],
    queryFn: async () => {
      let query = supabase
        .from('alert_history')
        .select(`
          *,
          location:locations(id, name),
          alert_rule:alert_rules(id, name)
        `)
        .eq('organization_id', profile!.organization_id)
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.severity) {
        query = query.eq('severity', filters.severity);
      }
      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate);
      }
      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate);
      }
      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as AlertHistory[];
    },
    enabled: !!profile?.organization_id,
  });
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('alert_history')
        .update({
          status: 'acknowledged',
          acknowledged_at: new Date().toISOString(),
          acknowledged_by: profile!.id,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-history'] });
    },
  });
}

export function useResolveAlert() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const { data, error } = await supabase
        .from('alert_history')
        .update({
          status: 'resolved',
          resolved_at: new Date().toISOString(),
          resolved_by: profile!.id,
          resolution_notes: notes,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-history'] });
    },
  });
}

// User Notifications hooks
export function useUserNotifications() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['user-notifications', profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', profile!.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data || []) as UserNotification[];
    },
    enabled: !!profile?.id,
  });
}

export function useUnreadNotificationCount() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['unread-notification-count', profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_unread_notification_count', {
        p_user_id: profile!.id,
      });

      if (error) {
        // Fallback if function doesn't exist
        const { count } = await supabase
          .from('user_notifications')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', profile!.id)
          .eq('is_read', false);
        
        return count || 0;
      }
      return data || 0;
    },
    enabled: !!profile?.id,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { data, error } = await supabase.rpc('mark_notifications_read', {
        p_notification_ids: ids,
      });

      if (error) {
        // Fallback if function doesn't exist
        const { error: updateError } = await supabase
          .from('user_notifications')
          .update({ is_read: true, read_at: new Date().toISOString() })
          .in('id', ids);
        
        if (updateError) throw updateError;
        return ids.length;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unread-notification-count'] });
    },
  });
}

// Helper to get rule type display name
export function getRuleTypeLabel(type: AlertRuleType): string {
  const labels: Record<AlertRuleType, string> = {
    order_waiting: 'Order Waiting Too Long',
    order_ready: 'Order Ready - Not Picked Up',
    kitchen_order_waiting: 'Kitchen Order Exceeds SLA',
    low_stock: 'Low Stock Alert',
    register_open: 'Register Inactive',
    shift_overtime: 'Shift Overtime',
    custom: 'Custom Rule',
  };
  return labels[type] || type;
}

// Helper to get severity color
export function getSeverityColor(severity: AlertSeverity): string {
  const colors: Record<AlertSeverity, string> = {
    info: 'bg-blue-100 text-blue-800',
    warning: 'bg-amber-100 text-amber-800',
    critical: 'bg-red-100 text-red-800',
  };
  return colors[severity] || 'bg-gray-100 text-gray-800';
}

